/**
 * Datas dos dias fixos de uma matrícula dentro de um intervalo.
 *
 * Convenção de dia da semana: 0=domingo … 6=sábado, igual a `Date.getDay()` e
 * a `scheduleModel.ts`. Aritmética em UTC pelo mesmo motivo de `stayPlans.ts`:
 * `YYYY-MM-DD` interpretado no fuso local escorrega um dia no horário de verão.
 */
export interface FixedDayRangeInput {
  from: string;
  to: string;
  weekdays: number[];
  /** 2 = quinzenal (semana sim, semana não). Padrão 1. */
  weekInterval?: number;
  /** Início do contrato: a semana dele é a primeira ativa. Obrigatório se weekInterval > 1. */
  startsOn?: string;
}

function toUtcDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error("invalid_calendar_date");
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

/** Segunda-feira (UTC) da semana de `date`. */
function mondayOf(date: Date): number {
  return date.getTime() - ((date.getUTCDay() + 6) % 7) * 86_400_000;
}

/**
 * A semana de `date` é de plano? Conta semanas (seg–dom) desde a do início.
 * Espelha `daycare_plan_week_is_active` no banco.
 */
export function isActivePlanWeek(startsOn: string, date: string, weekInterval: number): boolean {
  if (weekInterval <= 1) return true;
  const weeks = Math.round((mondayOf(toUtcDate(date)) - mondayOf(toUtcDate(startsOn))) / (7 * 86_400_000));
  return ((weeks % weekInterval) + weekInterval) % weekInterval === 0;
}

export function fixedDayDatesInRange({ from, to, weekdays, weekInterval = 1, startsOn }: FixedDayRangeInput): string[] {
  const start = toUtcDate(from);
  const end = toUtcDate(to);
  if (weekdays.length === 0 || start > end) return [];

  const wanted = new Set(weekdays);
  const dates: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const iso = toIsoDate(cursor);
    const activeWeek = weekInterval <= 1 || !startsOn || isActivePlanWeek(startsOn, iso, weekInterval);
    if (wanted.has(cursor.getUTCDay()) && activeWeek) dates.push(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

/** Primeira data >= `from` que cai num dos dias fixos. Sem dias, devolve `from`. */
export function snapToFixedDay(from: string, weekdays: number[]): string {
  if (weekdays.length === 0) return from;
  const cursor = toUtcDate(from);
  for (let step = 0; step < 7; step += 1) {
    if (weekdays.includes(cursor.getUTCDay())) return toIsoDate(cursor);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return from;
}

function addMonthsClampedUtc(value: string, months: number): string {
  const base = toUtcDate(value);
  const day = base.getUTCDate();
  const target = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return toIsoDate(target);
}

function dayBefore(value: string): string {
  return toIsoDate(new Date(toUtcDate(value).getTime() - 86_400_000));
}

/**
 * Datas do plano do ciclo que contém `from` e do seguinte: o mesmo horizonte
 * que `materialize_daycare_plan_cycle` mantém gerado. `anchor` = first_due_on.
 */
export function planDatesAhead({
  anchor,
  from,
  weekdays,
  weekInterval = 1,
  startsOn,
}: {
  anchor: string;
  from: string;
  weekdays: number[];
  weekInterval?: number;
  startsOn?: string;
}): string[] {
  let index = 0;
  while (index < 600 && from > dayBefore(addMonthsClampedUtc(anchor, index + 1))) {
    index += 1;
  }
  return fixedDayDatesInRange({ from, to: dayBefore(addMonthsClampedUtc(anchor, index + 2)), weekdays, weekInterval, startsOn: startsOn ?? anchor });
}
